import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { INFO, SUCCESS, WARNING, DANGER, propsUtil, svgUtil } from '@asphalt-react/helper';
import { sendStyles, Consumer } from '@asphalt-react/context';
import { Qualifier } from '@asphalt-react/qualifier';
import SvgNormalizer from '@asphalt-react/svg-normalizer';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

/**
 * size values
 */
const sizes = {
  s: "s",
  m: "m",
  l: "l"
};
const svgSizes = {
  sm: {
    height: "14",
    width: "14"
  },
  md: {
    height: "16",
    width: "16"
  },
  lg: {
    height: "20",
    width: "20"
  }
};

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Button__dtxrx {\n  /* default custom properties */\n  --button-border-radius: var(--roundness-1, 2px);\n  --button-primary-fill-color: var(\n    --interactive-fill-brand-default,\n    hsla(127, 100%, 33%, 1)\n  );\n  --button-secondary-fill-color: var(--interactive-fill-alternate, transparent);\n  --button-outline-fill-color: var(--interactive-fill-alternate, transparent);\n  --button-nude-fill-color: var(--interactive-fill-alternate, transparent);\n  --button-danger-fill-color: var(\n    --support-danger-default,\n    hsla(12, 92%, 45%, 1)\n  );\n\n  --button-primary-active-color: var(\n    --interactive-fill-brand-active,\n    hsla(127, 100%, 27%, 1)\n  );\n  --button-primary-hover-color: var(\n    --interactive-fill-brand-hover,\n    hsla(127, 57%, 47%, 1)\n  );\n  --button-disabled-color: var(--interactive-disabled, hsla(0, 0%, 86%, 1));\n\n  --button-secondary-active-color: var(\n    --interactive-fill-active,\n    hsla(220, 17%, 93%, 1)\n  );\n  --button-secondary-hover-color: var(\n    --interactive-fill-hover,\n    hsla(216, 24%, 96%, 1)\n  );\n\n  --button-danger-active-color: var(\n    --support-danger-active,\n    hsla(12, 92%, 36%, 1)\n  );\n  --button-danger-hover-color: var(\n    --support-danger-hover,\n    hsla(12, 76%, 56%, 1)\n  );\n\n  --button-outline-active-color: var(\n    --interactive-fill-selected-active,\n    hsla(219, 42%, 24%, 1)\n  );\n  --button-outline-hover-color: var(\n    --interactive-fill-selected-hover,\n    hsla(219, 23%, 44%, 1)\n  );\n\n  --button-nude-active-color: var(\n    --interactive-fill-active,\n    hsla(220, 17%, 93%, 1)\n  );\n  --button-nude-hover-color: var(\n    --interactive-fill-hover,\n    hsla(216, 24%, 96%, 1)\n  );\n\n  --button-primary-font: var(--text-inverse, hsla(0, 0%, 100%, 1));\n  --button-secondary-font: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  --button-danger-font: var(--text-inverse, hsla(0, 0%, 100%, 1));\n  --button-outline-font: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  --button-outline-hover-font: var(--text-inverse, hsla(0, 0%, 100%, 1));\n\n  --button-border-focus: var(--interactive-focus, hsla(219, 42%, 18%, 1));\n  --button-outline-border-inactive: var(\n    --interactive-border-action,\n    hsla(219, 42%, 31%, 1)\n  );\n  --button-outline-border-danger: var(\n    --interactive-border-support,\n    hsla(12, 92%, 36%, 1)\n  );\n\n  --button-border-width: var(--border-width, 1px);\n\n  --button-text-disabled: var(--text-disabled, hsla(0, 0%, 62%, 1));\n\n  --button-l-fontsize: var(--label-l, 1.13rem);\n  --button-m-fontsize: var(--label-m, 1rem);\n  --button-s-fontsize: var(--label-s, 0.88rem);\n  --nude-button-s-fontsize: var(--label-l, 1.13rem);\n  --nude-button-xs-fontsize: var(--label-m, 1rem);\n  --nude-button-xxs-fontsize: var(--label-s, 0.88rem);\n\n  --button-inverse-icon-color: var(--icon-inverse, hsla(0, 0%, 100%, 1));\n  --button-primary-icon-color: var(--icon-primary, hsla(220, 42%, 24%, 1));\n\n  --icon-disabled-color: var(--icon-disabled, hsla(0, 0%, 62%, 1));\n\n  /* custom support nude buttons */\n  --nude-info-fill-color: var(--support-info-default, hsla(209, 100%, 42%, 1));\n  --nude-info-hover-color: var(--support-info-hover, hsla(209, 72%, 54%, 1));\n  --nude-info-active-color: var(--support-info-active, hsla(209, 100%, 34%, 1));\n\n  --nude-success-fill-color: var(\n    --support-success-default,\n    hsla(159, 97%, 27%, 1)\n  );\n  --nude-success-hover-color: var(\n    --support-success-hover,\n    hsla(159, 51%, 41%, 1)\n  );\n  --nude-success-active-color: var(\n    --support-success-active,\n    hsla(159, 98%, 21%, 1)\n  );\n\n  --nude-warning-fill-color: var(\n    --support-warning-default,\n    hsla(33, 100%, 56%, 1)\n  );\n  --nude-warning-hover-color: var(\n    --support-warning-hover,\n    hsla(33, 100%, 65%, 1)\n  );\n  --nude-warning-active-color: var(\n    --support-warning-active,\n    hsla(33, 79%, 45%, 1)\n  );\n\n  --nude-danger-fill-color: var(\n    --support-danger-default,\n    hsla(12, 92%, 45%, 1)\n  );\n  --nude-danger-hover-color: var(--support-danger-hover, hsla(12, 76%, 56%, 1));\n  --nude-danger-active-color: var(\n    --support-danger-active,\n    hsla(12, 92%, 36%, 1)\n  );\n\n  --nude-support-font-color: var(--text-inverse, hsla(0, 0%, 100%, 1));\n\n  /* toggle button css variables */\n  --toggle-button-background-color: var(\n    --interactive-fill-alternate,\n    transparent\n  );\n  --toggle-button-font-color: var(--text-secondary, hsla(219, 42%, 24%, 1));\n\n  display: inline-flex;\n  justify-content: center;\n  white-space: nowrap;\n  text-align: center;\n  align-items: center;\n  border-color: transparent;\n  border-width: var(--button-border-width);\n  font-weight: 600;\n  line-height: initial;\n  border-radius: var(--button-border-radius);\n  box-sizing: border-box;\n  outline: none !important;\n  outline-style: none;\n}\n.Button__dtxrx.stretch__iy1gZ {\n  width: 100%;\n}\n\n/*  \n* Pointer events none is added to stop event propagation from svg (or path) back to the user \n*/\n\n.IconButton__EeuLT svg {\n  pointer-events: none;\n}\n\n/* primary styles */\n.primary__7r4wq {\n  background-color: var(--button-primary-fill-color);\n  color: var(--button-primary-font);\n}\n\n.primary__7r4wq svg path {\n  fill: var(--button-inverse-icon-color);\n}\n\n.primary__7r4wq:hover {\n  background-color: var(--button-primary-hover-color);\n}\n\n.primary__7r4wq:focus {\n  background-color: var(--button-primary-hover-color);\n  border-width: var(--button-border-width);\n  border-color: var(--button-border-focus);\n}\n\n.primary__7r4wq:active {\n  background-color: var(--button-primary-active-color);\n}\n\n/* secondary styles */\n.secondary__twuXR {\n  background-color: var(--button-secondary-fill-color);\n  color: var(--button-secondary-font);\n}\n\n.secondary__twuXR svg path,\n.outline__YB3Un svg path {\n  fill: var(--button-primary-icon-color);\n}\n\n.iconInheritsColor__qUH9n svg path {\n  fill: currentColor;\n}\n\n.secondary__twuXR:hover {\n  background-color: var(--button-secondary-hover-color);\n}\n\n.secondary__twuXR:focus {\n  background-color: var(--button-secondary-hover-color);\n  border-width: var(--button-border-width);\n  border-color: var(--button-border-focus);\n}\n\n.secondary__twuXR:active {\n  background-color: var(--button-secondary-active-color);\n}\n\n/* outline styles */\n.outline__YB3Un {\n  background-color: var(--button-outline-fill-color);\n  border: 1px solid var(--button-outline-border-inactive);\n  color: var(--button-outline-font);\n}\n\n.outline__YB3Un:hover {\n  background-color: var(--button-outline-hover-color);\n  color: var(--button-outline-hover-font);\n}\n\n.outline__YB3Un:focus {\n  background-color: var(--button-outline-hover-color);\n  border-width: var(--button-border-width);\n  border-color: var(--button-border-focus);\n  color: var(--button-outline-hover-font);\n}\n\n.outline__YB3Un:active {\n  background-color: var(--button-outline-active-color);\n}\n\n/* nude styles */\n.nude__gwht8 {\n  background: var(--button-nude-fill-color);\n  color: var(--button-secondary-font);\n}\n\n.nude__gwht8:hover {\n  background-color: var(--button-nude-hover-color);\n}\n\n.nude__gwht8:focus {\n  background-color: var(--button-nude-hover-color);\n  border-width: var(--button-border-width);\n  border-color: var(--button-border-focus);\n}\n\n.nude__gwht8:active {\n  background-color: var(--button-nude-active-color);\n}\n\n/* danger styles */\n.primary__7r4wq.danger__NVFDo,\n.IconButton__EeuLT.danger__NVFDo:not(.nude__gwht8) {\n  background-color: var(--button-danger-fill-color);\n  color: var(--button-danger-font);\n}\n\n.primary__7r4wq.danger__NVFDo svg path,\n.IconButton__EeuLT.danger__NVFDo svg path {\n  fill: var(--button-danger-font);\n}\n\n.primary__7r4wq.danger__NVFDo:hover,\n.outline__YB3Un.danger__NVFDo:hover,\n.IconButton__EeuLT.danger__NVFDo:not(.nude__gwht8):hover:enabled \n/* enabled applies when disabled exists. \nIt applies styles only when in enabled state   */ {\n  background-color: var(--button-danger-hover-color);\n}\n\n.primary__7r4wq.danger__NVFDo:focus,\n.outline__YB3Un.danger__NVFDo:focus,\n.IconButton__EeuLT.danger__NVFDo:not(.nude__gwht8):focus {\n  background-color: var(--button-danger-hover-color);\n  border-width: var(--button-border-width);\n  border-color: var(--button-border-focus);\n}\n\n.primary__7r4wq.danger__NVFDo:active,\n.outline__YB3Un.danger__NVFDo:active,\n.IconButton__EeuLT.danger__NVFDo:not(.nude__gwht8):active {\n  background-color: var(--button-danger-active-color);\n}\n\n.secondary__twuXR.danger__NVFDo,\n.nude__gwht8.danger__NVFDo,\n.outline__YB3Un.danger__NVFDo {\n  color: var(--button-danger-fill-color);\n}\n\n.secondary__twuXR.danger__NVFDo svg path,\n.nude__gwht8.danger__NVFDo svg path,\n.IconButton__EeuLT.nude__gwht8.danger__NVFDo svg path {\n  fill: var(--button-danger-fill-color);\n}\n\n.outline__YB3Un.danger__NVFDo {\n  border-color: var(--button-outline-border-danger);\n}\n\n.outline__YB3Un.danger__NVFDo:hover,\n.outline__YB3Un.danger__NVFDo:focus,\n.outline__YB3Un.danger__NVFDo:active {\n  color: var(--button-outline-hover-font);\n}\n\n/* disabled styles */\n.primary__7r4wq:disabled,\n.secondary__twuXR:disabled,\n.outline__YB3Un:disabled,\n.primary__7r4wq.danger__NVFDo:disabled,\n.outline__YB3Un.danger__NVFDo:disabled,\n.nude__gwht8:disabled {\n  background-color: var(--button-disabled-color);\n  color: var(--button-text-disabled);\n  cursor: not-allowed;\n  border-color: transparent;\n}\n\n.nude__gwht8:disabled {\n  background-color: transparent;\n}\n\n.primary__7r4wq:disabled svg path,\n.secondary__twuXR:disabled svg path,\n.nude__gwht8:disabled svg path,\n.danger__NVFDo:disabled svg path,\n.IconButton__EeuLT.nude__gwht8:disabled svg path {\n  fill: var(--icon-disabled-color);\n}\n\n/* \n* Pointer events none is added to stop event propagation from svg (or path) back to the user \n*/\n\n.qualifierContainer__xaGgg {\n  display: flex;\n  pointer-events: none;\n}\n\n.qualifierContainer__xaGgg {\n  margin-right: 0.5rem;\n}\n\n.end__keOvV {\n  flex-direction: row-reverse;\n}\n\n.end__keOvV > .qualifierContainer__xaGgg {\n  margin-left: 0.5rem;\n  margin-right: 0;\n}\n\n/* sizes */\n.l__ija4T {\n  padding: 0.75rem 1.25rem;\n  font-size: var(--button-l-fontsize);\n}\n\n.l__ija4T.qualifier__7Po6D {\n  padding: 0.75rem;\n}\n\n.m__72G7g {\n  padding: 0.5rem 1rem;\n  font-size: var(--button-m-fontsize);\n}\n\n.m__72G7g.qualifier__7Po6D {\n  padding: 0.5rem;\n}\n\n.s__hxReC {\n  padding: 0.25rem 0.75rem;\n  font-size: var(--button-s-fontsize);\n}\n\n.s__hxReC.qualifier__7Po6D {\n  padding: 0.25rem;\n}\n\n.outline__YB3Un.l__ija4T,\n.nude__gwht8.l__ija4T {\n  padding-top: 0.25rem;\n  padding-bottom: 0.25rem;\n}\n\n.outline__YB3Un.m__72G7g,\n.nude__gwht8.m__72G7g {\n  padding-top: 0.125rem;\n  padding-bottom: 0.125rem;\n}\n\n.outline__YB3Un.s__hxReC,\n.nude__gwht8.s__hxReC {\n  padding-top: 0;\n  padding-bottom: 0;\n}\n\n.IconButton__EeuLT.l__ija4T {\n  padding: 1rem;\n}\n\n.IconButton__EeuLT.m__72G7g {\n  padding: 0.75rem 1rem;\n}\n\n.IconButton__EeuLT.s__hxReC {\n  padding: 0.5rem 1rem;\n}\n\n.IconButton__EeuLT.nude__gwht8 {\n  padding: 0.25rem;\n}\n\n/* support nude button styles for Flag component */\n.nude__gwht8.support__hlgwX {\n  color: var(--nude-support-font-color);\n}\n\n/* danger */\n.nude__gwht8.support__hlgwX.supportDanger__vubrl {\n  background-color: var(--nude-danger-fill-color);\n}\n\n.nude__gwht8.support__hlgwX.supportDanger__vubrl:hover {\n  background-color: var(--nude-danger-hover-color);\n}\n\n.nude__gwht8.support__hlgwX.supportDanger__vubrl:active {\n  background-color: var(--nude-danger-active-color);\n}\n\n/* warning */\n.nude__gwht8.support__hlgwX.supportWarning__--1ul {\n  background-color: var(--nude-warning-fill-color);\n}\n\n.nude__gwht8.support__hlgwX.supportWarning__--1ul:hover {\n  background-color: var(--nude-warning-hover-color);\n}\n\n.nude__gwht8.support__hlgwX.supportWarning__--1ul:active {\n  background-color: var(--nude-warning-active-color);\n}\n\n/* success */\n.nude__gwht8.support__hlgwX.supportSuccess__MyDs3 {\n  background-color: var(--nude-success-fill-color);\n}\n\n.nude__gwht8.support__hlgwX.supportSuccess__MyDs3:hover {\n  background-color: var(--nude-success-hover-color);\n}\n\n.nude__gwht8.support__hlgwX.supportSuccess__MyDs3:active {\n  background-color: var(--nude-success-active-color);\n}\n\n/* info */\n.nude__gwht8.support__hlgwX.supportInfo__63yXY {\n  background-color: var(--nude-info-fill-color);\n}\n\n.nude__gwht8.support__hlgwX.supportInfo__63yXY:hover {\n  background-color: var(--nude-info-hover-color);\n}\n\n.nude__gwht8.support__hlgwX.supportInfo__63yXY:active {\n  background-color: var(--nude-info-active-color);\n}\n\n/* toggle button styles */\n.toggleButton__gAz3- {\n  background-color: var(--toggle-button-background-color);\n  color: var(--toggle-button-font-color);\n  padding: 0.5px 1.67rem;\n  font-size: var(--button-m-fontsize);\n}\n\n.smallToggle__OKiE- {\n  padding: 0 1.47rem;\n  font-size: var(--button-s-fontsize);\n}\n\n.largeToggle__Ct2e7 {\n  padding: 0.065rem 1.92rem;\n  font-size: var(--button-l-fontsize);\n}\n\n.iconToggle__MBu7B {\n  padding: 0.188rem;\n}\n\n.smallIconToggle__MIVZB {\n  padding: 0.125rem;\n  font-size: var(--button-s-fontsize);\n}\n\n.largeIconToggle__5ewyQ {\n  font-size: var(--button-l-fontsize);\n}\n\n.toggleButton__gAz3-:hover,\n.toggleButton__gAz3-:focus {\n  --toggle-button-background-color: var(\n    --interactive-fill-hover,\n    hsla(216, 24%, 96%, 1)\n  );\n}\n\n.toggleButton__gAz3-:focus {\n  border-color: var(--interactive-focus, hsla(219, 42%, 18%, 1));\n}\n\n.on__GqssJ {\n  --toggle-button-background-color: var(\n    --interactive-fill-selected-default,\n    hsla(219, 42%, 31%, 1)\n  );\n  --toggle-button-font-color: var(--text-inverse, hsla(0, 0%, 100%, 1));\n}\n\n.on__GqssJ svg path {\n  fill: var(--icon-inverse, hsla(0, 0%, 100%, 1));\n}\n\n.on__GqssJ:hover,\n.on__GqssJ:focus {\n  --toggle-button-background-color: var(\n    --interactive-fill-selected-hover,\n    hsla(219, 23%, 44%, 1)\n  );\n}\n\n.toggleButton__gAz3-:disabled,\n.on__GqssJ:disabled {\n  --toggle-button-font-color: var(--text-disabled, hsla(0, 0%, 62%, 1));\n  --toggle-button-background-color: var(\n    --interactive-fill-alternate,\n    transparent\n  );\n}\n\n.on__GqssJ:disabled {\n  --toggle-button-background-color: var(\n    --interactive-disabled,\n    hsla(0, 0%, 86%, 1)\n  );\n  --toggle-button-font-color: var(--text-disabled, hsla(0, 0%, 62%, 1));\n}\n\n.link__x1aOq {\n  border-style: solid;\n  text-decoration: underline;\n}\n\n/* rtl styles */\n*[dir=\"rtl\"] .qualifierContainer__xaGgg {\n  margin-left: 0.5rem;\n  margin-right: 0;\n}\n\n*[dir=\"rtl\"] .end__keOvV > .qualifierContainer__xaGgg {\n  margin-right: 0.5rem;\n  margin-left: 0;\n}\n";
var styles = {"Button":"Button__dtxrx","stretch":"stretch__iy1gZ","IconButton":"IconButton__EeuLT","primary":"primary__7r4wq","secondary":"secondary__twuXR","outline":"outline__YB3Un","iconInheritsColor":"iconInheritsColor__qUH9n","nude":"nude__gwht8","danger":"danger__NVFDo","qualifierContainer":"qualifierContainer__xaGgg","end":"end__keOvV","l":"l__ija4T","qualifier":"qualifier__7Po6D","m":"m__72G7g","s":"s__hxReC","support":"support__hlgwX","supportDanger":"supportDanger__vubrl","supportWarning":"supportWarning__--1ul","supportSuccess":"supportSuccess__MyDs3","supportInfo":"supportInfo__63yXY","toggleButton":"toggleButton__gAz3-","smallToggle":"smallToggle__OKiE-","largeToggle":"largeToggle__Ct2e7","iconToggle":"iconToggle__MBu7B","smallIconToggle":"smallIconToggle__MIVZB","largeIconToggle":"largeIconToggle__5ewyQ","on":"on__GqssJ","link":"link__x1aOq"};
var stylesheet=".Button__dtxrx {\n  /* default custom properties */\n  --button-border-radius: var(--roundness-1, 2px);\n  --button-primary-fill-color: var(\n    --interactive-fill-brand-default,\n    hsla(127, 100%, 33%, 1)\n  );\n  --button-secondary-fill-color: var(--interactive-fill-alternate, transparent);\n  --button-outline-fill-color: var(--interactive-fill-alternate, transparent);\n  --button-nude-fill-color: var(--interactive-fill-alternate, transparent);\n  --button-danger-fill-color: var(\n    --support-danger-default,\n    hsla(12, 92%, 45%, 1)\n  );\n\n  --button-primary-active-color: var(\n    --interactive-fill-brand-active,\n    hsla(127, 100%, 27%, 1)\n  );\n  --button-primary-hover-color: var(\n    --interactive-fill-brand-hover,\n    hsla(127, 57%, 47%, 1)\n  );\n  --button-disabled-color: var(--interactive-disabled, hsla(0, 0%, 86%, 1));\n\n  --button-secondary-active-color: var(\n    --interactive-fill-active,\n    hsla(220, 17%, 93%, 1)\n  );\n  --button-secondary-hover-color: var(\n    --interactive-fill-hover,\n    hsla(216, 24%, 96%, 1)\n  );\n\n  --button-danger-active-color: var(\n    --support-danger-active,\n    hsla(12, 92%, 36%, 1)\n  );\n  --button-danger-hover-color: var(\n    --support-danger-hover,\n    hsla(12, 76%, 56%, 1)\n  );\n\n  --button-outline-active-color: var(\n    --interactive-fill-selected-active,\n    hsla(219, 42%, 24%, 1)\n  );\n  --button-outline-hover-color: var(\n    --interactive-fill-selected-hover,\n    hsla(219, 23%, 44%, 1)\n  );\n\n  --button-nude-active-color: var(\n    --interactive-fill-active,\n    hsla(220, 17%, 93%, 1)\n  );\n  --button-nude-hover-color: var(\n    --interactive-fill-hover,\n    hsla(216, 24%, 96%, 1)\n  );\n\n  --button-primary-font: var(--text-inverse, hsla(0, 0%, 100%, 1));\n  --button-secondary-font: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  --button-danger-font: var(--text-inverse, hsla(0, 0%, 100%, 1));\n  --button-outline-font: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  --button-outline-hover-font: var(--text-inverse, hsla(0, 0%, 100%, 1));\n\n  --button-border-focus: var(--interactive-focus, hsla(219, 42%, 18%, 1));\n  --button-outline-border-inactive: var(\n    --interactive-border-action,\n    hsla(219, 42%, 31%, 1)\n  );\n  --button-outline-border-danger: var(\n    --interactive-border-support,\n    hsla(12, 92%, 36%, 1)\n  );\n\n  --button-border-width: var(--border-width, 1px);\n\n  --button-text-disabled: var(--text-disabled, hsla(0, 0%, 62%, 1));\n\n  --button-l-fontsize: var(--label-l, 1.13rem);\n  --button-m-fontsize: var(--label-m, 1rem);\n  --button-s-fontsize: var(--label-s, 0.88rem);\n  --nude-button-s-fontsize: var(--label-l, 1.13rem);\n  --nude-button-xs-fontsize: var(--label-m, 1rem);\n  --nude-button-xxs-fontsize: var(--label-s, 0.88rem);\n\n  --button-inverse-icon-color: var(--icon-inverse, hsla(0, 0%, 100%, 1));\n  --button-primary-icon-color: var(--icon-primary, hsla(220, 42%, 24%, 1));\n\n  --icon-disabled-color: var(--icon-disabled, hsla(0, 0%, 62%, 1));\n\n  /* custom support nude buttons */\n  --nude-info-fill-color: var(--support-info-default, hsla(209, 100%, 42%, 1));\n  --nude-info-hover-color: var(--support-info-hover, hsla(209, 72%, 54%, 1));\n  --nude-info-active-color: var(--support-info-active, hsla(209, 100%, 34%, 1));\n\n  --nude-success-fill-color: var(\n    --support-success-default,\n    hsla(159, 97%, 27%, 1)\n  );\n  --nude-success-hover-color: var(\n    --support-success-hover,\n    hsla(159, 51%, 41%, 1)\n  );\n  --nude-success-active-color: var(\n    --support-success-active,\n    hsla(159, 98%, 21%, 1)\n  );\n\n  --nude-warning-fill-color: var(\n    --support-warning-default,\n    hsla(33, 100%, 56%, 1)\n  );\n  --nude-warning-hover-color: var(\n    --support-warning-hover,\n    hsla(33, 100%, 65%, 1)\n  );\n  --nude-warning-active-color: var(\n    --support-warning-active,\n    hsla(33, 79%, 45%, 1)\n  );\n\n  --nude-danger-fill-color: var(\n    --support-danger-default,\n    hsla(12, 92%, 45%, 1)\n  );\n  --nude-danger-hover-color: var(--support-danger-hover, hsla(12, 76%, 56%, 1));\n  --nude-danger-active-color: var(\n    --support-danger-active,\n    hsla(12, 92%, 36%, 1)\n  );\n\n  --nude-support-font-color: var(--text-inverse, hsla(0, 0%, 100%, 1));\n\n  /* toggle button css variables */\n  --toggle-button-background-color: var(\n    --interactive-fill-alternate,\n    transparent\n  );\n  --toggle-button-font-color: var(--text-secondary, hsla(219, 42%, 24%, 1));\n\n  display: inline-flex;\n  justify-content: center;\n  white-space: nowrap;\n  text-align: center;\n  align-items: center;\n  border-color: transparent;\n  border-width: var(--button-border-width);\n  font-weight: 600;\n  line-height: initial;\n  border-radius: var(--button-border-radius);\n  box-sizing: border-box;\n  outline: none !important;\n  outline-style: none;\n}\n.Button__dtxrx.stretch__iy1gZ {\n  width: 100%;\n}\n\n/*  \n* Pointer events none is added to stop event propagation from svg (or path) back to the user \n*/\n\n.IconButton__EeuLT svg {\n  pointer-events: none;\n}\n\n/* primary styles */\n.primary__7r4wq {\n  background-color: var(--button-primary-fill-color);\n  color: var(--button-primary-font);\n}\n\n.primary__7r4wq svg path {\n  fill: var(--button-inverse-icon-color);\n}\n\n.primary__7r4wq:hover {\n  background-color: var(--button-primary-hover-color);\n}\n\n.primary__7r4wq:focus {\n  background-color: var(--button-primary-hover-color);\n  border-width: var(--button-border-width);\n  border-color: var(--button-border-focus);\n}\n\n.primary__7r4wq:active {\n  background-color: var(--button-primary-active-color);\n}\n\n/* secondary styles */\n.secondary__twuXR {\n  background-color: var(--button-secondary-fill-color);\n  color: var(--button-secondary-font);\n}\n\n.secondary__twuXR svg path,\n.outline__YB3Un svg path {\n  fill: var(--button-primary-icon-color);\n}\n\n.iconInheritsColor__qUH9n svg path {\n  fill: currentColor;\n}\n\n.secondary__twuXR:hover {\n  background-color: var(--button-secondary-hover-color);\n}\n\n.secondary__twuXR:focus {\n  background-color: var(--button-secondary-hover-color);\n  border-width: var(--button-border-width);\n  border-color: var(--button-border-focus);\n}\n\n.secondary__twuXR:active {\n  background-color: var(--button-secondary-active-color);\n}\n\n/* outline styles */\n.outline__YB3Un {\n  background-color: var(--button-outline-fill-color);\n  border: 1px solid var(--button-outline-border-inactive);\n  color: var(--button-outline-font);\n}\n\n.outline__YB3Un:hover {\n  background-color: var(--button-outline-hover-color);\n  color: var(--button-outline-hover-font);\n}\n\n.outline__YB3Un:focus {\n  background-color: var(--button-outline-hover-color);\n  border-width: var(--button-border-width);\n  border-color: var(--button-border-focus);\n  color: var(--button-outline-hover-font);\n}\n\n.outline__YB3Un:active {\n  background-color: var(--button-outline-active-color);\n}\n\n/* nude styles */\n.nude__gwht8 {\n  background: var(--button-nude-fill-color);\n  color: var(--button-secondary-font);\n}\n\n.nude__gwht8:hover {\n  background-color: var(--button-nude-hover-color);\n}\n\n.nude__gwht8:focus {\n  background-color: var(--button-nude-hover-color);\n  border-width: var(--button-border-width);\n  border-color: var(--button-border-focus);\n}\n\n.nude__gwht8:active {\n  background-color: var(--button-nude-active-color);\n}\n\n/* danger styles */\n.primary__7r4wq.danger__NVFDo,\n.IconButton__EeuLT.danger__NVFDo:not(.nude__gwht8) {\n  background-color: var(--button-danger-fill-color);\n  color: var(--button-danger-font);\n}\n\n.primary__7r4wq.danger__NVFDo svg path,\n.IconButton__EeuLT.danger__NVFDo svg path {\n  fill: var(--button-danger-font);\n}\n\n.primary__7r4wq.danger__NVFDo:hover,\n.outline__YB3Un.danger__NVFDo:hover,\n.IconButton__EeuLT.danger__NVFDo:not(.nude__gwht8):hover:enabled \n/* enabled applies when disabled exists. \nIt applies styles only when in enabled state   */ {\n  background-color: var(--button-danger-hover-color);\n}\n\n.primary__7r4wq.danger__NVFDo:focus,\n.outline__YB3Un.danger__NVFDo:focus,\n.IconButton__EeuLT.danger__NVFDo:not(.nude__gwht8):focus {\n  background-color: var(--button-danger-hover-color);\n  border-width: var(--button-border-width);\n  border-color: var(--button-border-focus);\n}\n\n.primary__7r4wq.danger__NVFDo:active,\n.outline__YB3Un.danger__NVFDo:active,\n.IconButton__EeuLT.danger__NVFDo:not(.nude__gwht8):active {\n  background-color: var(--button-danger-active-color);\n}\n\n.secondary__twuXR.danger__NVFDo,\n.nude__gwht8.danger__NVFDo,\n.outline__YB3Un.danger__NVFDo {\n  color: var(--button-danger-fill-color);\n}\n\n.secondary__twuXR.danger__NVFDo svg path,\n.nude__gwht8.danger__NVFDo svg path,\n.IconButton__EeuLT.nude__gwht8.danger__NVFDo svg path {\n  fill: var(--button-danger-fill-color);\n}\n\n.outline__YB3Un.danger__NVFDo {\n  border-color: var(--button-outline-border-danger);\n}\n\n.outline__YB3Un.danger__NVFDo:hover,\n.outline__YB3Un.danger__NVFDo:focus,\n.outline__YB3Un.danger__NVFDo:active {\n  color: var(--button-outline-hover-font);\n}\n\n/* disabled styles */\n.primary__7r4wq:disabled,\n.secondary__twuXR:disabled,\n.outline__YB3Un:disabled,\n.primary__7r4wq.danger__NVFDo:disabled,\n.outline__YB3Un.danger__NVFDo:disabled,\n.nude__gwht8:disabled {\n  background-color: var(--button-disabled-color);\n  color: var(--button-text-disabled);\n  cursor: not-allowed;\n  border-color: transparent;\n}\n\n.nude__gwht8:disabled {\n  background-color: transparent;\n}\n\n.primary__7r4wq:disabled svg path,\n.secondary__twuXR:disabled svg path,\n.nude__gwht8:disabled svg path,\n.danger__NVFDo:disabled svg path,\n.IconButton__EeuLT.nude__gwht8:disabled svg path {\n  fill: var(--icon-disabled-color);\n}\n\n/* \n* Pointer events none is added to stop event propagation from svg (or path) back to the user \n*/\n\n.qualifierContainer__xaGgg {\n  display: flex;\n  pointer-events: none;\n}\n\n.qualifierContainer__xaGgg {\n  margin-right: 0.5rem;\n}\n\n.end__keOvV {\n  flex-direction: row-reverse;\n}\n\n.end__keOvV > .qualifierContainer__xaGgg {\n  margin-left: 0.5rem;\n  margin-right: 0;\n}\n\n/* sizes */\n.l__ija4T {\n  padding: 0.75rem 1.25rem;\n  font-size: var(--button-l-fontsize);\n}\n\n.l__ija4T.qualifier__7Po6D {\n  padding: 0.75rem;\n}\n\n.m__72G7g {\n  padding: 0.5rem 1rem;\n  font-size: var(--button-m-fontsize);\n}\n\n.m__72G7g.qualifier__7Po6D {\n  padding: 0.5rem;\n}\n\n.s__hxReC {\n  padding: 0.25rem 0.75rem;\n  font-size: var(--button-s-fontsize);\n}\n\n.s__hxReC.qualifier__7Po6D {\n  padding: 0.25rem;\n}\n\n.outline__YB3Un.l__ija4T,\n.nude__gwht8.l__ija4T {\n  padding-top: 0.25rem;\n  padding-bottom: 0.25rem;\n}\n\n.outline__YB3Un.m__72G7g,\n.nude__gwht8.m__72G7g {\n  padding-top: 0.125rem;\n  padding-bottom: 0.125rem;\n}\n\n.outline__YB3Un.s__hxReC,\n.nude__gwht8.s__hxReC {\n  padding-top: 0;\n  padding-bottom: 0;\n}\n\n.IconButton__EeuLT.l__ija4T {\n  padding: 1rem;\n}\n\n.IconButton__EeuLT.m__72G7g {\n  padding: 0.75rem 1rem;\n}\n\n.IconButton__EeuLT.s__hxReC {\n  padding: 0.5rem 1rem;\n}\n\n.IconButton__EeuLT.nude__gwht8 {\n  padding: 0.25rem;\n}\n\n/* support nude button styles for Flag component */\n.nude__gwht8.support__hlgwX {\n  color: var(--nude-support-font-color);\n}\n\n/* danger */\n.nude__gwht8.support__hlgwX.supportDanger__vubrl {\n  background-color: var(--nude-danger-fill-color);\n}\n\n.nude__gwht8.support__hlgwX.supportDanger__vubrl:hover {\n  background-color: var(--nude-danger-hover-color);\n}\n\n.nude__gwht8.support__hlgwX.supportDanger__vubrl:active {\n  background-color: var(--nude-danger-active-color);\n}\n\n/* warning */\n.nude__gwht8.support__hlgwX.supportWarning__--1ul {\n  background-color: var(--nude-warning-fill-color);\n}\n\n.nude__gwht8.support__hlgwX.supportWarning__--1ul:hover {\n  background-color: var(--nude-warning-hover-color);\n}\n\n.nude__gwht8.support__hlgwX.supportWarning__--1ul:active {\n  background-color: var(--nude-warning-active-color);\n}\n\n/* success */\n.nude__gwht8.support__hlgwX.supportSuccess__MyDs3 {\n  background-color: var(--nude-success-fill-color);\n}\n\n.nude__gwht8.support__hlgwX.supportSuccess__MyDs3:hover {\n  background-color: var(--nude-success-hover-color);\n}\n\n.nude__gwht8.support__hlgwX.supportSuccess__MyDs3:active {\n  background-color: var(--nude-success-active-color);\n}\n\n/* info */\n.nude__gwht8.support__hlgwX.supportInfo__63yXY {\n  background-color: var(--nude-info-fill-color);\n}\n\n.nude__gwht8.support__hlgwX.supportInfo__63yXY:hover {\n  background-color: var(--nude-info-hover-color);\n}\n\n.nude__gwht8.support__hlgwX.supportInfo__63yXY:active {\n  background-color: var(--nude-info-active-color);\n}\n\n/* toggle button styles */\n.toggleButton__gAz3- {\n  background-color: var(--toggle-button-background-color);\n  color: var(--toggle-button-font-color);\n  padding: 0.5px 1.67rem;\n  font-size: var(--button-m-fontsize);\n}\n\n.smallToggle__OKiE- {\n  padding: 0 1.47rem;\n  font-size: var(--button-s-fontsize);\n}\n\n.largeToggle__Ct2e7 {\n  padding: 0.065rem 1.92rem;\n  font-size: var(--button-l-fontsize);\n}\n\n.iconToggle__MBu7B {\n  padding: 0.188rem;\n}\n\n.smallIconToggle__MIVZB {\n  padding: 0.125rem;\n  font-size: var(--button-s-fontsize);\n}\n\n.largeIconToggle__5ewyQ {\n  font-size: var(--button-l-fontsize);\n}\n\n.toggleButton__gAz3-:hover,\n.toggleButton__gAz3-:focus {\n  --toggle-button-background-color: var(\n    --interactive-fill-hover,\n    hsla(216, 24%, 96%, 1)\n  );\n}\n\n.toggleButton__gAz3-:focus {\n  border-color: var(--interactive-focus, hsla(219, 42%, 18%, 1));\n}\n\n.on__GqssJ {\n  --toggle-button-background-color: var(\n    --interactive-fill-selected-default,\n    hsla(219, 42%, 31%, 1)\n  );\n  --toggle-button-font-color: var(--text-inverse, hsla(0, 0%, 100%, 1));\n}\n\n.on__GqssJ svg path {\n  fill: var(--icon-inverse, hsla(0, 0%, 100%, 1));\n}\n\n.on__GqssJ:hover,\n.on__GqssJ:focus {\n  --toggle-button-background-color: var(\n    --interactive-fill-selected-hover,\n    hsla(219, 23%, 44%, 1)\n  );\n}\n\n.toggleButton__gAz3-:disabled,\n.on__GqssJ:disabled {\n  --toggle-button-font-color: var(--text-disabled, hsla(0, 0%, 62%, 1));\n  --toggle-button-background-color: var(\n    --interactive-fill-alternate,\n    transparent\n  );\n}\n\n.on__GqssJ:disabled {\n  --toggle-button-background-color: var(\n    --interactive-disabled,\n    hsla(0, 0%, 86%, 1)\n  );\n  --toggle-button-font-color: var(--text-disabled, hsla(0, 0%, 62%, 1));\n}\n\n.link__x1aOq {\n  border-style: solid;\n  text-decoration: underline;\n}\n\n/* rtl styles */\n*[dir=\"rtl\"] .qualifierContainer__xaGgg {\n  margin-left: 0.5rem;\n  margin-right: 0;\n}\n\n*[dir=\"rtl\"] .end__keOvV > .qualifierContainer__xaGgg {\n  margin-right: 0.5rem;\n  margin-left: 0;\n}\n";
styleInject(css_248z);

const sizeClass = size => {
  switch (size) {
    case "l":
      return styles.l;

    case "m":
      return styles.m;

    case "s":
      return styles.s;

    default:
      return styles.m;
  }
};
/**
 * Create css classes from props
 *
 * @param {object} param custom props object
 * @returns {string}
 */


const createBtnClasses = ({
  size
}) => {
  return cn(styles.Button, sizeClass(size));
};
const svgSize = size => {
  switch (size) {
    case "s":
      return svgSizes.sm;

    case "m":
      return svgSizes.md;

    case "l":
      return svgSizes.lg;

    default:
      return svgSizes.md;
  }
};
/**
 * Determines if a button is large sized
 *
 * @param {Object} sizes available sizes
 * @param {string} size size of button
 *
 * @returns {Boolean}
 */

const isLarge = (sizes, size) => size === sizes.l;
/**
 * Determines if a button is small sized
 *
 * @param {Object} sizes available sizes
 * @param {string} size size of button
 *
 * @returns {Boolean}
 */

const isSmall = (sizes, size) => size === sizes.s;
/**
 * Determines if a link opens in a new tab (or window)
 *
 * Reference: https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types/noopener
 *
 * @param {string} target target attribute
 * @returns {boolean}
 */

const opensInNewTab = target => target && !(target === "_self" || target === "_top" || target === "_parent");
/**
 * Returns `rel` attribute if link opens in a new tab
 * to avoid [security and privacy risks](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#security_and_privacy)
 *
 * @param {string} target target attribute
 * @returns {Object}
 */

const getRelAttributes = target => opensInNewTab(target) ? {
  rel: "noreferrer"
} : {};

const {
  createPropOccurValidator: createPropOccurValidator$3
} = propsUtil;
const Button = /*#__PURE__*/forwardRef(({
  children,
  size,
  primary,
  secondary,
  outline,
  danger,
  qualifier,
  qualifierStart,
  qualifierEnd,
  nude,
  stretch,
  iconInheritsColor,
  ...props
}, ref) => {
  //Send styles to server
  sendStyles(stylesheet);

  if (outline && qualifier) {
    console.warn("Outline Button cannot have qualifiers. Added qualifier will be ignored");
    qualifier = false;
  }
  /**
   * Returns style of Button
   *
   * if more than one of the below are present, falls back to `styles.secondary`
   * if nothing is provided, takes `styles.primary`
   */


  const selectBtnTypeClass = () => {
    const validateBtnProp = createPropOccurValidator$3(["primary", "secondary", "outline", "nude"]);
    const singleOccur = validateBtnProp({
      primary,
      secondary,
      outline,
      nude
    });

    if (!singleOccur) {
      return styles.secondary;
    }

    if (secondary) {
      return styles.secondary;
    }

    if (outline) {
      return styles.outline;
    }

    if (nude) {
      return styles.nude;
    }

    return styles.primary;
  };

  const getSupportClass = supportType => {
    switch (supportType) {
      case DANGER:
        return styles.supportDanger;

      case WARNING:
        return styles.supportWarning;

      case SUCCESS:
        return styles.supportSuccess;

      case INFO:
        return styles.supportInfo;

      default:
        return null;
    }
  }; // filtering out style and className

  /* eslint-disable-next-line react/prop-types */


  const {
    style,
    className,
    ...rest
  } = props;
  const btnclasses = cn(createBtnClasses({
    size
  }), selectBtnTypeClass(), {
    [styles.danger]: danger
  }, {
    [styles.stretch]: stretch
  }, {
    [styles.qualifier]: qualifier
  }, {
    [styles.end]: qualifier && qualifierEnd
  }, {
    [styles.iconInheritsColor]: iconInheritsColor
  });
  return qualifier ? /*#__PURE__*/React.createElement("button", _extends({}, rest, {
    className: btnclasses,
    ref: ref
  }), /*#__PURE__*/React.createElement("span", {
    className: cn(styles.qualifierContainer)
  }, /*#__PURE__*/React.createElement(Qualifier, {
    iconSize: svgSize(size)
  }, qualifier)), children) : /*#__PURE__*/React.createElement(Consumer, null, ({
    supportType
  }) => {
    const supportClasses = cn(btnclasses, {
      [styles.support]: Boolean(supportType)
    }, getSupportClass(supportType));
    return /*#__PURE__*/React.createElement("button", _extends({}, rest, {
      className: supportClasses,
      ref: ref
    }), children);
  });
});
Button.displayName = "Button";
Button.propTypes = {
  /**
   * React node for button's caption
   */
  children: PropTypes.node.isRequired,

  /**
   * Controls size of button. Possible values are "s", "m", "l" for small, medium & large respectively
   */
  size: PropTypes.oneOf(["s", "m", "l"]),

  /**
   * Button matches width of it's container
   */
  stretch: PropTypes.bool,

  /**
   * Renders a nude button
   */
  nude: PropTypes.bool,

  /**
   * Applies primary brand color
   */
  primary: PropTypes.bool,

  /**
   * Renders a secondary button
   */
  secondary: PropTypes.bool,

  /**
   * Renders an outline button
   */
  outline: PropTypes.bool,

  /**
   * Renders as danger button
   */
  danger: PropTypes.bool,

  /**
   * Qualifiers provide a hint about the action a button will perform. Appends (default) or prepends an icon to the button's caption.
   * Accepts SVG or string.
   *
   * Checkout `@asphalt-react/iconpack` for SVG wrapped React components.
   */
  qualifier: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),

  /**
   * Renders qualifier before the button's caption
   */
  qualifierStart: PropTypes.bool,

  /**
   * Renders qualifier after the button's caption. Has priority over `qualifierStart`.
   */
  qualifierEnd: PropTypes.bool,

  /**
   * Icons inherits Button’s `color` property. Use it to override the icon’s default color with the Button’s caption color.
   */
  iconInheritsColor: PropTypes.bool
};
Button.defaultProps = {
  size: "m",
  stretch: false,
  qualifier: null,
  nude: false,
  qualifierStart: true,
  qualifierEnd: false,
  iconInheritsColor: false
};

const {
  createPropOccurValidator: createPropOccurValidator$2
} = propsUtil;
const IconButton = /*#__PURE__*/forwardRef(({
  icon,
  size,
  primary,
  danger,
  nude,
  ...props
}, ref) => {
  //Send styles to server
  sendStyles(stylesheet);
  /**
   * Returns style of Button
   *
   * if `primary` and `nude' both are preset,
   * falls back to `styles.primary`
   *
   * @returns {Object}
   */

  const selectBtnTypeClass = () => {
    const validateBtnPropOccur = createPropOccurValidator$2(["primary", "nude"]);
    const singleOccur = validateBtnPropOccur({
      primary,
      nude
    });

    if (!singleOccur) {
      return styles.primary;
    }

    if (nude) {
      return styles.nude;
    }

    return styles.primary;
  }; // filtering out style and className

  /* eslint-disable-next-line react/prop-types */


  const {
    style,
    className,
    ...rest
  } = props;
  const btnclasses = cn(createBtnClasses({
    size
  }), selectBtnTypeClass(), styles.IconButton, {
    [styles.danger]: danger
  });
  return /*#__PURE__*/React.createElement("button", _extends({}, rest, {
    className: btnclasses,
    ref: ref
  }), /*#__PURE__*/React.createElement(SvgNormalizer, {
    size: svgSize(size)
  }, icon));
});
IconButton.displayName = "IconButton";
IconButton.propTypes = {
  /**
   * Renders an icon for the caption. Accepts an inline SVG or SVG wrapped React component
   */
  icon: PropTypes.element.isRequired,

  /**
   * Applies primary brand color
   */
  primary: PropTypes.bool,

  /**
   * Renders as danger button
   */
  danger: PropTypes.bool,

  /**
   * Renders a nude icon button
   */
  nude: PropTypes.bool,

  /**
   * Determines the size. Possible values are "s", "m", "l" for small, medium & large respectively/
   */
  size: PropTypes.oneOf(["s", "m", "l"])
};
IconButton.defaultProps = {
  size: "m",
  nude: false
};

const ToggleButton = ({
  text,
  children,
  icon,
  size,
  on,
  ...props
}) => {
  //Send styles to server
  sendStyles(stylesheet); // filtering out style and className

  /* eslint-disable-next-line react/prop-types */

  const {
    style,
    className,
    ...rest
  } = props;

  const sizeClass = () => {
    const small = isSmall(sizes, size);
    const large = isLarge(sizes, size);

    if (icon) {
      return [{
        [styles.smallIconToggle]: small
      }, {
        [styles.largeIconToggle]: large
      }];
    }

    return [{
      [styles.smallToggle]: small
    }, {
      [styles.largeToggle]: large
    }];
  };

  if (icon && !svgUtil.isSVG(icon)) {
    console.error("ToggleButton: Icon can only be an SVG or an SVG wrapped React component");
    return null;
  }

  return /*#__PURE__*/React.createElement("button", _extends({}, rest, {
    type: "button",
    role: "switch",
    "aria-checked": on,
    className: cn(styles.Button, styles.toggleButton, {
      [styles.iconToggle]: icon
    }, {
      [styles.on]: on
    }, sizeClass())
  }), icon ? /*#__PURE__*/React.createElement(SvgNormalizer, {
    size: svgSize(size)
  }, icon) : text || children);
};
ToggleButton.propTypes = {
  /**
   * Label for text-only variant
   */
  text: PropTypes.string,

  /**
   * Icon for icon-only variant. Accepts SVG or SVG wrapped React component
   *
   * **Note:** `icon` prop overrides `text` prop
   */
  icon: PropTypes.node,

  /**
   * React node for caption
   *
   * **Note:** Both `icon` and `text` props override `children`
   */
  children: PropTypes.node,

  /**
   * Sets the state of button
   */
  on: PropTypes.bool,

  /**
   * Sets the size of button. Possible values are `"s", "m", "l"` for small, medium & large respectively
   */
  size: PropTypes.oneOf(["s", "m", "l"])
};
ToggleButton.defaultProps = {
  on: false,
  size: "m"
};

const NewTab = () => /*#__PURE__*/React.createElement("svg", {
  height: "16",
  width: "16",
  fill: "none",
  viewBox: "0 0 16 16",
  xmlns: "http://www.w3.org/2000/svg",
  "data-testid": "new-tab-icon"
}, /*#__PURE__*/React.createElement("path", {
  d: "M2.77772 13.2222L2.77772 2.55556L8.04509 2.55556C8.28084 2.55556 8.50693 2.46191 8.67363 2.29521C9.23116 1.73767 8.84043 0.786085 8.05538 0.777832H1.88889C1.4 0.777832 1 1.17783 1 1.66672V14.1112C1 14.3585 1.10235 14.583 1.26677 14.7446C1.4274 14.9023 1.64727 15 1.88883 15L14.3333 15C14.8222 15 15.2222 14.6 15.2222 14.1111V7.94459C15.2139 7.15956 14.2623 6.76884 13.7048 7.32637C13.5381 7.49307 13.4444 7.71916 13.4444 7.95491V13.2223H2.77778V13.2222H2.77772Z",
  fill: "#000",
  fillRule: "evenodd"
}), /*#__PURE__*/React.createElement("path", {
  d: "M15.1023 0H11.5467C11.0578 0 10.6578 0.39975 10.6578 0.888334C10.6578 1.37692 11.0578 1.77667 11.5467 1.77667H12.9601L10.0356 4.69929C9.87561 4.85919 9.77783 5.08127 9.77783 5.33C9.77783 5.81859 10.1778 6.21834 10.6667 6.21834C10.9156 6.21834 11.1378 6.12062 11.2978 5.96072L14.2223 3.0381V4.45055C14.2223 4.93914 14.6223 5.33889 15.1112 5.33889C15.6001 5.33889 16.0001 4.93914 16.0001 4.45055V0.897217C15.9912 0.39975 15.5912 0 15.1023 0Z",
  fill: "#000",
  fillRule: "evenodd"
}));
NewTab.iconName = "NewTab";

const {
  createPropOccurValidator: createPropOccurValidator$1
} = propsUtil;
const ButtonLink = /*#__PURE__*/forwardRef(({
  children,
  size,
  primary,
  secondary,
  qualifier,
  as: Link,
  tagProps,
  qualifierEnd
}, ref) => {
  //Send styles to server
  sendStyles(stylesheet);
  /**
   * Add NewTab icon as default to hint that the link opens in a new tab (or window)
   */

  if (opensInNewTab(tagProps === null || tagProps === void 0 ? void 0 : tagProps.target) && !qualifier) {
    qualifier = /*#__PURE__*/React.createElement(NewTab, null);
  } // filtering out `style` prop from `tagProps`


  const {
    style,
    ...restTagProps
  } = tagProps;
  /**
   * Returns style of ButtonLink
   *
   * renders secondary variant if there are multiple variants
   * falls back to primary variant if none are present
   *
   * @returns {Object}
   */

  const selectBtnTypeClass = () => {
    const validateBtnProp = createPropOccurValidator$1(["primary", "secondary"]);
    const singleOccur = validateBtnProp({
      primary,
      secondary
    });

    if (!singleOccur) {
      return styles.secondary;
    }

    if (secondary) {
      return styles.secondary;
    }

    return styles.primary;
  }; // generate classes


  const btnclasses = cn(createBtnClasses({
    size
  }), selectBtnTypeClass(), styles.link, {
    [styles.qualifier]: qualifier
  }, {
    [styles.end]: qualifier && qualifierEnd
  });
  return /*#__PURE__*/React.createElement(Link, _extends({}, getRelAttributes(tagProps === null || tagProps === void 0 ? void 0 : tagProps.target), restTagProps, {
    className: btnclasses,
    ref: ref
  }), qualifier && /*#__PURE__*/React.createElement("span", {
    className: cn(styles.qualifierContainer)
  }, /*#__PURE__*/React.createElement(Qualifier, {
    iconSize: svgSize(size)
  }, qualifier)), children && children);
});
ButtonLink.displayName = "ButtonLink";
ButtonLink.propTypes = {
  /**
   * React node for the caption.
   */
  children: PropTypes.node.isRequired,

  /**
   * Accepts props & attributes for the link element.
   */
  tagProps: PropTypes.object.isRequired,

  /**
   * Link element to render. Accepts an HTML element or a React component.
   */
  as: PropTypes.elementType,

  /**
   * Size of ButtonLink. Possible values are "s", "m", "l" for small, medium & large respectively.
   */
  size: PropTypes.oneOf(["s", "m", "l"]),

  /**
   * Applies primary brand styles.
   */
  primary: PropTypes.bool,

  /**
   * Applies secondary styles.
   */
  secondary: PropTypes.bool,

  /**
   * Qualifiers provide a hint about the action a ButtonLink will perform. Appends or prepends (default) an icon to the caption.
   * Accepts SVG or string.
   *
   * Checkout `@asphalt-react/iconpack` for SVG wrapped React components.
   */
  qualifier: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),

  /**
   * Appends qualifier to the caption.
   */
  qualifierEnd: PropTypes.bool
};
ButtonLink.defaultProps = {
  size: "m",
  as: "a",
  qualifier: null,
  qualifierEnd: false
};

const {
  createPropOccurValidator
} = propsUtil;
const IconButtonLink = /*#__PURE__*/forwardRef(({
  icon,
  size,
  primary,
  nude,
  as: Link,
  tagProps
}, ref) => {
  //Send styles to server
  sendStyles(stylesheet);
  /**
   * Returns style of Button
   *
   * renders primary variant if there are no or multiple variants
   *
   * @returns {Object}
   */

  const selectBtnTypeClass = () => {
    const validateBtnPropOccur = createPropOccurValidator(["primary", "nude"]);
    const singleOccur = validateBtnPropOccur({
      primary,
      nude
    });

    if (!singleOccur) {
      return styles.primary;
    }

    if (nude) {
      return styles.nude;
    }

    return styles.primary;
  }; // filtering out `style` prop from `tagProps`


  const {
    style,
    ...restTagProps
  } = tagProps; // generate classes

  const btnclasses = cn(createBtnClasses({
    size
  }), selectBtnTypeClass(), styles.IconButton, styles.link); // show warning if icon is not an SVG

  if (icon && !svgUtil.isSVG(icon)) {
    console.warn("IconButtonLink: `icon` can only be an SVG");
    icon = null;
  }

  return /*#__PURE__*/React.createElement(Link, _extends({}, getRelAttributes(tagProps === null || tagProps === void 0 ? void 0 : tagProps.target), restTagProps, {
    className: btnclasses,
    ref: ref
  }), icon && /*#__PURE__*/React.createElement(SvgNormalizer, {
    size: svgSize(size)
  }, icon));
});
IconButtonLink.displayName = "IconButtonLink";
IconButtonLink.propTypes = {
  /**
   * Renders an icon for the caption. Accepts an inline SVG or SVG wrapped React component
   *
   * Checkout `@asphalt-react/iconpack` for SVG wrapped React components.
   */
  icon: PropTypes.element.isRequired,

  /**
   * Accepts props & attributes for the link element
   */
  tagProps: PropTypes.object.isRequired,

  /**
   * Link element to render. Accepts an HTML element or a React component.
   */
  as: PropTypes.elementType,

  /**
   * Applies primary brand styles.
   */
  primary: PropTypes.bool,

  /**
   * Applies nude styles
   */
  nude: PropTypes.bool,

  /**
   * Size of IconButtonLink. Possible values are "s", "m", "l" for small, medium & large respectively
   */
  size: PropTypes.oneOf(["s", "m", "l"])
};
IconButtonLink.defaultProps = {
  size: "m",
  as: "a"
};

export { ButtonLink, IconButton, IconButtonLink, ToggleButton, Button as default };
